/**
 * AutopilotWeeklyReport — Karte mit dem letzten Autopilot-Wochenbericht (Meta + Google).
 * Wird jeden Sonntag ab 17:00 automatisch generiert und per E-Mail versendet.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, FileBarChart, TrendingUp, TrendingDown, PiggyBank } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtMoney = (n) => `CHF ${Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-CH"); } catch { return iso; } };

function Delta({ v, invert }) {
  if (v === null || v === undefined) return null;
  const up = v > 0;
  const good = invert ? v < 0 : v > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] ${v === 0 ? "text-gray-400" : good ? "text-emerald-600" : "text-red-500"}`}>
      {up ? <TrendingUp className="w-2.5 h-2.5" /> : v < 0 ? <TrendingDown className="w-2.5 h-2.5" /> : null}
      {Math.abs(v).toFixed(1)}%
    </span>
  );
}

function PlatformLine({ label, sec }) {
  if (!sec || (!sec.connected && !(sec.paused || []).length && !sec.suggestions)) return null;
  const perf = sec.perf;
  const deltas = sec.deltas || {};
  const leadKey = perf && "leads" in perf ? "leads" : "conversions";
  return (
    <div className="text-xs border-t border-dashed first:border-0 pt-1.5 first:pt-0">
      <span className="font-semibold">{label}:</span>{" "}
      {(sec.paused || []).length > 0 ? (
        <span>⏸ {sec.paused.length} pausiert{sec.savings > 0 && <span className="text-emerald-600 font-medium"> · ~{fmtMoney(sec.savings)}/Wo gespart</span>}</span>
      ) : sec.suggestions > 0 ? (
        <span>💡 {sec.suggestions} Vorschläge</span>
      ) : (
        <span className="text-gray-500">keine Autopilot-Aktionen</span>
      )}
      {perf && (
        <span className="text-gray-600">
          {" · "}Ausgaben {fmtMoney(perf.spend)} <Delta v={deltas.spend} invert />
          {" · "}Klicks {perf.clicks} <Delta v={deltas.clicks} />
          {" · "}CTR {Number(perf.ctr || 0).toFixed(2)}% <Delta v={deltas.ctr} />
          {" · "}{leadKey === "leads" ? "Leads" : "Conv."} {perf[leadKey] ?? 0} <Delta v={deltas[leadKey]} />
        </span>
      )}
    </div>
  );
}

export default function AutopilotWeeklyReport() {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/ads/autopilot/weekly-report`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ reports: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/ads/autopilot/weekly-report/send`, {}, { withCredentials: true });
      toast.success(r.data.message, { duration: 7000 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bericht fehlgeschlagen"); }
    finally { setSending(false); }
  };

  const reports = data?.reports || [];
  const totalSavings = reports.reduce((s, r) => s + (r.savings_total || 0), 0);

  return (
    <Card data-testid="autopilot-weekly-report-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <FileBarChart className="w-4 h-4 text-emerald-600" /> Autopilot-Wochenbericht
          <Badge variant="outline" className="text-[10px]"><Mail className="w-2.5 h-2.5 mr-0.5" /> jeden Sonntag 17:00 per E-Mail</Badge>
          {totalSavings > 0 && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid="report-total-savings">
              <PiggyBank className="w-2.5 h-2.5 mr-0.5" /> ~{fmtMoney(totalSavings)} gespart
            </Badge>
          )}
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={sendNow} disabled={sending} data-testid="report-send-now-btn">
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Jetzt erstellen & senden
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {data === null && <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>}
        {data !== null && reports.length === 0 && (
          <div className="text-xs text-[var(--studio-text-muted)]" data-testid="report-empty">
            Noch kein Wochenbericht erstellt. Der Bericht wird jeden Sonntag automatisch generiert — oder klicke «Jetzt erstellen & senden».
          </div>
        )}
        {reports.map((r) => {
          const drafts = r.drafts || {};
          return (
            <div key={r.id} className="border rounded-lg p-3 space-y-1.5" data-testid={`report-company-${r.company_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-sm">{r.company_name}</span>
                {r.savings_total > 0 && (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">~{fmtMoney(r.savings_total)} gespart</Badge>
                )}
                <span className="text-[10px] text-gray-400 ml-auto">Woche {fmtDate(r.week_from)} – {fmtDate(r.week_to)} · erstellt {fmtDate(r.generated_at)}</span>
              </div>
              <PlatformLine label="Meta" sec={r.meta} />
              <PlatformLine label="Google" sec={r.google} />
              {((drafts.created || []).length > 0 || (drafts.approved || []).length > 0 || (drafts.rejected || []).length > 0) && (
                <div className="text-xs text-gray-600 border-t border-dashed pt-1.5">
                  {(drafts.created || []).length > 0 && <span>✨ {drafts.created.length} neue Entwürfe </span>}
                  {(drafts.approved || []).length > 0 && <span>· ✅ {drafts.approved.length} freigegeben </span>}
                  {(drafts.rejected || []).length > 0 && <span>· ❌ {drafts.rejected.length} abgelehnt</span>}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
